import { useEffect, useState } from 'react';

// local
import { PostViewAdmin } from './post-view.jsx';

// mui
import {
    Box,
    Chip,
    Divider,
    Stack,
    TableContainer,
    Table,
    TableRow,
    TableBody,
    TableCell,
    Typography,
} from '@mui/material';

// client-shared
import { ContainerScroll } from '@goldblack/client-comps';
import { def } from '@goldblack/shared';
import { adminDeepLink } from '../..';

const { ReportStatus } = def;

const STATUS_COLOR = {
    [ReportStatus.PENDING]: 'warning',
    [ReportStatus.RESOLVED]: 'success',
    [ReportStatus.DISMISSED]: 'default',
    [ReportStatus.DELETED]: 'error',
};

const STATUS_LABEL = {
    [ReportStatus.PENDING]: 'pending',
    [ReportStatus.RESOLVED]: 'resolved',
    [ReportStatus.DISMISSED]: 'dismissed',
    [ReportStatus.DELETED]: 'deleted',
};

function UserCell({ user, onClick }) {
    if (!user?.username) return null;
    return (
        <Stack
            direction='row'
            alignItems='center'
            justifyContent='flex-end'
            spacing={1}
            onClick={onClick}
            sx={onClick ? { cursor: 'pointer', '&:hover': { color: 'primary.main' } } : undefined}>
            {(user.thumbnail || user.image) && (
                <img
                    src={user.thumbnail || user.image}
                    alt=''
                    width={24}
                    height={24}
                    style={{ borderRadius: '50%' }}
                />
            )}
            <Typography variant='body2'>{user.username}</Typography>
        </Stack>
    );
}

function PostCell({ post, onClick }) {
    if (!post) return null;
    const mediaSrc = post.thumbnail || post.image;
    return (
        <Stack direction='column' alignItems='flex-end' spacing={1}>
            {mediaSrc && (
                <img
                    src={mediaSrc}
                    alt=''
                    style={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: 4,
                        cursor: 'pointer',
                        display: 'block',
                    }}
                    onClick={onClick}
                />
            )}
            {post.desc && (
                <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>
                    {post.desc}
                </Typography>
            )}
        </Stack>
    );
}

function AdminReportInfoPanel({ report, pendingStatus = null, onSetStatus, onDeepLink }) {
    const [dateString, setDateString] = useState('');
    const [processDateString, setProcessDateString] = useState('');
    const [openPost, setOpenPost] = useState(false);
    const [viewPost, setViewPost] = useState(null);

    useEffect(() => {
        if (report) {
            setDateString(report.report_at ? new Date(report.report_at).toLocaleString('ko-KR') : '');
            setProcessDateString(report.process_at ? new Date(report.process_at).toLocaleString('ko-KR') : '—');
        }
        setOpenPost(false);
    }, [report]);

    if (!report) return <></>;

    const isPost = report.type === 'post';
    const isComment = report.type === 'comment';
    const isUser = !report.type || report.type === 'user';

    const onClickPost = () => {
        setViewPost(report.targetPost);
        setOpenPost(true);
    };

    const onPostStatusChange = (updatedPost) => {
        setViewPost(updatedPost);
    };

    return (
        <>
            <ContainerScroll>
                <TableContainer>
                    <Table sx={{ '& td, & th': { border: 1, borderColor: 'divider' } }}>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ width: '30%' }}>id</TableCell>
                                <TableCell align='right'>
                                    <Typography sx={{ fontSize: '14px', wordBreak: 'break-all' }}>
                                        {report._id}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>type</TableCell>
                                <TableCell align='right'>
                                    <Chip label={report.type || 'user'} size='small' variant='outlined' />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>status</TableCell>
                                <TableCell align='right'>
                                    <Chip
                                        label={
                                            STATUS_LABEL[pendingStatus ?? report.status] ??
                                            pendingStatus ??
                                            report.status
                                        }
                                        size='small'
                                        color={STATUS_COLOR[pendingStatus ?? report.status] || 'default'}
                                        variant={pendingStatus !== null ? 'outlined' : 'filled'}
                                    />
                                </TableCell>
                            </TableRow>
                            {report.result && (
                                <TableRow>
                                    <TableCell>result</TableCell>
                                    <TableCell align='right'>{report.result}</TableCell>
                                </TableRow>
                            )}
                            <TableRow>
                                <TableCell>reason</TableCell>
                                <TableCell align='right'>{report.reason}</TableCell>
                            </TableRow>
                            {isPost && (
                                <TableRow>
                                    <TableCell>post</TableCell>
                                    <TableCell align='right'>
                                        {report.targetPost ? (
                                            <PostCell post={report.targetPost} onClick={onClickPost} />
                                        ) : report.snapshot ? (
                                            <PostCell post={report.snapshot} />
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                            )}
                            {isComment && (
                                <TableRow>
                                    <TableCell>comment</TableCell>
                                    <TableCell align='right'>
                                        <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>
                                            {report.targetComment?.content || report.snapshot?.content || ''}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {report.type === 'message' && report.snapshot?.message && (
                                <TableRow>
                                    <TableCell>message</TableCell>
                                    <TableCell align='right'>
                                        <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>
                                            {report.snapshot.message}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            <TableRow>
                                <TableCell>reporter</TableCell>
                                <TableCell align='right'>
                                    <UserCell
                                        user={report.reporterUser}
                                        onClick={
                                            report.reporterUser?.username && onDeepLink
                                                ? () =>
                                                      onDeepLink(
                                                          adminDeepLink.loise.users.search({
                                                              username: report.reporterUser.username,
                                                          }),
                                                      )
                                                : undefined
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                            {(isPost || isComment || isUser) && report.targetUser?.username && (
                                <TableRow>
                                    <TableCell>target</TableCell>
                                    <TableCell align='right'>
                                        <UserCell
                                            user={report.targetUser}
                                            onClick={
                                                onDeepLink
                                                    ? () =>
                                                          onDeepLink(
                                                              adminDeepLink.loise.users.search({
                                                                  username: report.targetUser.username,
                                                              }),
                                                          )
                                                    : undefined
                                            }
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                            {report.moderation && (
                                <TableRow>
                                    <TableCell sx={{ verticalAlign: 'top' }}>AI</TableCell>
                                    <TableCell sx={{ p: 0 }}>
                                        <Stack spacing={0} divider={<Divider sx={{ borderColor: 'white' }} />}>
                                            <Stack
                                                direction='row'
                                                justifyContent='space-between'
                                                alignItems='center'
                                                spacing={1}
                                                sx={{ px: 2, py: 1 }}>
                                                <Typography variant='body2' color='text.secondary'>
                                                    action
                                                </Typography>
                                                <Chip
                                                    label={report.moderation.action}
                                                    size='small'
                                                    color={
                                                        report.moderation.action === 'censored' ? 'error' : 'default'
                                                    }
                                                    variant='outlined'
                                                />
                                            </Stack>
                                            <Stack
                                                direction='row'
                                                justifyContent='space-between'
                                                alignItems='center'
                                                spacing={1}
                                                sx={{ px: 2, py: 1 }}>
                                                <Typography variant='body2' color='text.secondary'>
                                                    categories
                                                </Typography>
                                                <Stack
                                                    direction='row'
                                                    spacing={0.5}
                                                    flexWrap='wrap'
                                                    justifyContent='flex-end'>
                                                    {report.moderation.categories.length > 0 ? (
                                                        report.moderation.categories.map((cat) => (
                                                            <Chip
                                                                key={cat}
                                                                label={cat}
                                                                size='small'
                                                                variant='outlined'
                                                            />
                                                        ))
                                                    ) : (
                                                        <Typography variant='body2' color='text.secondary'>
                                                            none
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Stack>
                                            <Stack
                                                direction='row'
                                                justifyContent='space-between'
                                                alignItems='center'
                                                spacing={1}
                                                sx={{ px: 2, py: 1 }}>
                                                <Typography variant='body2' color='text.secondary'>
                                                    severity
                                                </Typography>
                                                <Typography variant='body2'>{report.moderation.severity}</Typography>
                                            </Stack>
                                            <Stack
                                                direction='row'
                                                justifyContent='space-between'
                                                alignItems='center'
                                                spacing={1}
                                                sx={{ px: 2, py: 1 }}>
                                                <Typography variant='body2' color='text.secondary'>
                                                    confidence
                                                </Typography>
                                                <Typography variant='body2'>
                                                    {(report.moderation.confidence * 100).toFixed(0)}%
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            )}
                            <TableRow>
                                <TableCell>date</TableCell>
                                <TableCell align='right'>{dateString}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>process date</TableCell>
                                <TableCell align='right'>{processDateString}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </ContainerScroll>

            <PostViewAdmin
                open={openPost}
                onClose={() => setOpenPost(false)}
                post={viewPost}
                user={{
                    username: report.targetUser?.username,
                    image: report.targetUser?.image,
                    thumbnail: report.targetUser?.thumbnail,
                    badges: report.targetUser?.badges,
                }}
                onStatusChange={onPostStatusChange}
            />
        </>
    );
}

export { AdminReportInfoPanel };
